import React, { useEffect, useState, useRef, useContext } from "react";
import { connect } from "react-redux";
import { Box } from "@mui/material";
import { getMappings, addMapping, removeMapping } from "requests/employeeCustomerSurveyTypes";
import MGrid, { ACTION_ADD, ACTION_DELETE, ACTION_EDIT, ACTION_CANCEL, FILTER_TYPE_TEXT } from "components/Shared/MGrid";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { userHasAnyRight } from "selectors/rights";
import { FIELD_SCHEDULING_MAPPING_EDIT, QA_TEAM_MAINTENANCE } from "typeConstants/rights";
import AddMapping, { QueueType } from "./AddMapping";
import CopyMappingsDialogContent from "./CopyMappingsDialogContent";
import { EmployeeCustomerSurveyTypeDto, EmployeeCustomerSurveyTypeGetResponse, EmployeeDetailDto } from "interfaces/Mueller.Business.Models";
import { ACTION_CLONE } from "components/Shared/MDialog";
import { getEmployeeDetailsByEmployeeId, getEmployeesByRoles } from "requests/employee";
import responseHandler from "responseHandler";

interface Props {
  employeeId: number,
  overrideQueueTypes?: QueueType[],
  cardStyle?: boolean,
  cardTitleOverride?: string,
  // Redux
  canEdit: boolean,
  makeSuccessToast: (text: string) => void,
  makeErrorToast: (text: string) => void,
}

const EmployeeMappings = (props: Props) => {
  const [mappings, setMappings] = useState<EmployeeCustomerSurveyTypeGetResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [isFetchingEmployees, setIsFetchingEmployees] = useState<boolean>(true);
  const [employeeCopyOptions, setEmployeeCopyOptions] = useState<EmployeeDetailDto[]>([]);
  const [isCopyingEmployeeMappings, setIsCopyingEmployeeMappings] = useState<boolean>(false);
  const dialog: DialogProviderValue = useContext(DialogContext);
  const mounted = useRef(true);

  // need to store these in a ref because they are used in the display for the grid, and that function gets cached in mgrid
  const excludedRef = useRef<EmployeeCustomerSurveyTypeDto[]>([]);

  excludedRef.current = mappings.filter(m => m.isExcluded);
  
  useEffect(() => {
    getEmployeeDetailsByEmployeeId(props.employeeId, (response: EmployeeDetailDto) => {
      getEmployeesByRoles([response.roleId], (response: EmployeeDetailDto[]) => {
        setEmployeeCopyOptions(response);
        setIsFetchingEmployees(false);
      }, mounted);
    }, mounted);

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    refreshMappings();
  }, [props.employeeId]);

  const successCallback = (mappings: EmployeeCustomerSurveyTypeGetResponse[]) => {
    setMappings(mappings);
    setLoading(false);
  };

  const errorCallback = () => {
    setLoading(false);
  };

  const refreshMappings = () => {
    if (selected.length) {
      setSelected([]);
      setMappings([]);
    }
    getMappings(props.employeeId, successCallback, errorCallback, mounted);
  };


  const getExcludedLabel = (row: EmployeeCustomerSurveyTypeDto) => {
    if (row.isExcluded) return "Yes";
    let isPartiallyExcluded = excludedRef.current.some(
      em => em.queueTypeCode === row.queueTypeCode &&
            (
              (!em.customerNumber && !em.surveyTypeCode) || // fully excluded from everything... why would we do this?
              ((!row.customerNumber || !em.customerNumber) && em.surveyTypeCode === row.surveyTypeCode) ||
              ((!row.surveyTypeCode || !em.surveyTypeCode) && em.customerNumber === row.customerNumber)
            )
          );
    if (isPartiallyExcluded) {
      return "No*";
    } else {
      return "No";
    }
  };

  const columns = [
    {
      key: "queueType",
      name: "Queue",
      width: 120,
      dataSource: "queueTypeCode",
      resizable: false,
      filterType: FILTER_TYPE_TEXT
    },
    {
      key: "surveyType",
      name: "Survey Type",
      width: 200,
      dataSource: (row: EmployeeCustomerSurveyTypeDto) => !row.surveyType ? "ALL" : row.surveyType,
      resizable: true,
      filterType: FILTER_TYPE_TEXT
    },
    {
      key: "customer",
      name: "Customer",
      width: 200,
      dataSource: (row: EmployeeCustomerSurveyTypeDto) => !row.customerNumber ? "ALL" : `(${row.customerNumber}) ${row.customerName}`,
      resizable: true,
      filterType: FILTER_TYPE_TEXT
    },
    {
      key: "isExcluded",
      name: "Is Excluded",
      width: 110,
      dataSource: getExcludedLabel,
      resizable: true,
    },
  ];

  const getActions = () => {
    let actions = null;
    if (editing) {
      actions = [
        { code: ACTION_CANCEL, onClick: onCancel },
        { code: ACTION_ADD, onClick: onAdd },
      ];
      if (selected.length > 0) {
        actions.push({ code: ACTION_DELETE, onClick: onDelete });
      }
    } else {
      actions = [
        { code: ACTION_ADD, onClick: onAdd, helpText: "Add Mappings" },
        { code: ACTION_EDIT, onClick: onEdit, helpText: "Edit Mappings" },
        { code: ACTION_CLONE, onClick: onCopy, helpText: "Copy Mappings" },
      ];
    }
    return actions;
  };

  const onAdd = () => {
    const dialogProps = { className: "add-employee-mapping-dialog"};
    dialog.show("Add Mapping", <AddMapping onCancel={dialog.hide} onSubmit={onAddSubmit} overrideQueueTypes={props.overrideQueueTypes} />, null, dialogProps);
  };

  const onEdit = () => {
    setEditing(true);
  };

  const onCopy = () => {
    dialog.show(
      "Copy Mappings",
      <CopyMappingsDialogContent 
        onCopyMappingsClick={onCopyMappingsClick}
        employeeCopyOptions={employeeCopyOptions}
      />,
    );
  };

  const onCopyMappingsClick = (employeeIdToCopy: number) => {
    setIsCopyingEmployeeMappings(true);
    dialog.hide();
    //Get all mappings of the person we want to copy from
    getMappings(
      employeeIdToCopy,
      (mappings) => {
        //On success we use their mappings to add mappings to the currently selected person
        addMapping(
          props.employeeId,
          mappings,
          () => {
            refreshMappings();
            setIsCopyingEmployeeMappings(false);
            props.makeSuccessToast("Mappings copied successfully!");
          },
          () => {
            setIsCopyingEmployeeMappings(false);
            props.makeErrorToast("Copy failed, please try again");
          },
          mounted
        );
      },
      () => {
        props.makeErrorToast("Failed to retrieve mappings to copy, please try again");
      },
      mounted
    );
  };

  const onCancel = () => {
    setSelected([]);
    setEditing(false);
  };

  const onAddSubmit = (mappings: EmployeeCustomerSurveyTypeDto[]) => {
    setLoading(true);
    addMapping(props.employeeId, mappings, addCallback, addCallback, mounted);
  };

  const addCallback = () => {
    dialog.hide(),
    refreshMappings();
  };

  const onDelete = () => {
    dialog.showYesNo(
      "Confirm Remove Mappings",
      `Do you want to remove ${selected.length} mapping${selected.length > 1 ? "s": ""}? This action cannot be undone.`,
      onConfirmDelete,
      dialog.hide,
      "Remove",
      "Cancel");
  };

  const onConfirmDelete = () => {
    setLoading(true);
    let payload: number[] = [];
    selected.forEach(i => {
      payload.push(mappings[i].id);
    });
    removeMapping(
      props.employeeId,
      payload,
      () => {
        refreshMappings();
        props.makeSuccessToast("Mappings deleted successfully!");
      },
      () => {
        refreshMappings();
        props.makeErrorToast("Failed to delete selected mappings, please try again");
      },
      mounted
    );
  };

  return (
    <Box className="md-paper--1" sx={{ height: 500, width: "100%", overflow: "hidden" }}>
      <MGrid
        headerText={props.cardTitleOverride ?? "Employee Mappings"}
        columnDefinitions={columns}
        data={mappings}
        isFetchingData={loading || isFetchingEmployees || isCopyingEmployeeMappings}
        displayNumOfItemsFound={true}
        actions={props.canEdit ? getActions() : null}
        allowRowSelection={editing}
        onRowSelectionChanged={setSelected}
        selectedRows={selected}
        cardStyle={props.cardStyle}
      />
    </Box>
  );
};

const mapStateToProps = state => {
  return {
    canEdit: userHasAnyRight(FIELD_SCHEDULING_MAPPING_EDIT, QA_TEAM_MAINTENANCE)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeSuccessToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeMappings);